import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Space, Input, Row, Col, Modal, Form, Select, message, Upload } from "antd";
import { PrinterOutlined, PlusOutlined, UploadOutlined, FilePdfOutlined, FileExcelOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const Santha = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [memberOptions, setMemberOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [selectedSanthaFilter, setSelectedSanthaFilter] = useState(null);
  const [santhaOptions, setSanthaOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const yearsub = Form.useWatch("yearsub", form);
  const meetingfee = Form.useWatch("meetingfee", form);
  const adminFee = Form.useWatch("Adminfee", form);
  const selectedMemberId = Form.useWatch("memberId", form);
  const santhaType = Form.useWatch("santhaname", form);

  // Memoize filtered transactions
  const filteredTransactions = useMemo(
    () =>
      data.filter(
        (record) =>
          record.memberId?._id === selectedMemberId &&
          record.santhaname === santhaType
      ),
    [data, selectedMemberId, santhaType]
  );

  useEffect(() => {
    fetchData();
    fetchMembers();
    fetchSanthaMaster();
  }, []);

  useEffect(() => {
    if (!selectedMemberId) {
      form.setFieldsValue({ groupincharge: "", outstanding: "0.00" });
      return;
    }

    const member = memberOptions.find((m) => m.value === selectedMemberId);
    const latestTransaction = filteredTransactions.sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    )[0];

    const initialOutstanding = form.getFieldValue("tobeCollect") || "0.00";
    const previousOutstanding = latestTransaction
      ? parseFloat(latestTransaction.outstanding)
      : parseFloat(initialOutstanding);

    const outstanding = (
      previousOutstanding -
      ((parseFloat(adminFee) || 0) + (parseFloat(yearsub) || 0) + (parseFloat(meetingfee) || 0))
    ).toFixed(2);

    form.setFieldsValue({
      groupincharge: member?.groupincharge || "",
      outstanding: isNaN(outstanding) ? "0.00" : outstanding,
    });
  }, [selectedMemberId, santhaType, memberOptions, adminFee, yearsub, meetingfee, form, filteredTransactions]);

  const fetchSanthaMaster = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/getsanthamaster");
      setSanthaOptions(response.data.data.map((s) => ({ value: s._id, label: s.santhaname })));
    } catch (error) {
      message.error("Failed to fetch Santha master data.");
    }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/getsantha");
      const sortedData = (response.data.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setData(sortedData);
      setFilteredData(sortedData);
    } catch (error) {
      message.error("Failed to fetch transaction data.");
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await axios.get("http://103.61.224.178:9876/member/get-all-members");
      const sortedMembers = response.data.data
        .filter((m) => m.Accno && m.Name)
        .sort((a, b) => {
          const numA = parseInt(a.Accno.replace(/\D/g, ""), 10);
          const numB = parseInt(b.Accno.replace(/\D/g, ""), 10);
          return numA - numB;
        });

      setMemberOptions(
        sortedMembers.map((m) => ({
          value: m._id,
          label: `${m.Accno} (${m.Name})`,
          groupincharge: m.groupincharge || "",
        }))
      );
    } catch (error) {
      message.error("Failed to fetch member data.");
    }
  };

  // Enhanced search functionality
  useEffect(() => {
    if (!Array.isArray(data)) return;

    let filtered = [...data];

    // Apply Santha filter if selected
    if (selectedSanthaFilter) {
      filtered = filtered.filter((item) => item.santhaname?.toString() === selectedSanthaFilter);
    }

    // Apply search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        // Get member details from memberOptions
        const member = memberOptions.find((m) => m.value === item.memberId?._id);
        const santha = santhaOptions.find((s) => s.value === item.santhaname);

        // Create an array of all searchable values, including nested and derived fields
        const searchableValues = [
          item.memberId?.Accno || "", // ACCI Code
          member?.label || "", // Member Name (from memberOptions)
          member?.groupincharge || "", // Group Incharge (from memberOptions)
          santha?.label || "", // Santha Type (from santhaOptions)
          item.Adminfee?.toString() || "", // Admin Fee
          item.yearsub?.toString() || "", // Year Subscription
          item.meetingfee?.toString() || "", // Meeting Fee
          item.other?.toString() || "", // Other
          item.tobeCollect?.toString() || "", // To Be Collect
          item.outstanding?.toString() || "", // Outstanding
          item.remarks || "", // Remarks
          item.invoice || "", // Invoice Number
        ];

        // Check if any searchable value includes the search text
        return searchableValues.some((value) =>
          value.toLowerCase().includes(search)
        );
      });
    }

    setFilteredData(filtered);
  }, [searchText, selectedSanthaFilter, data, memberOptions, santhaOptions]);

  const handleAddOrUpdate = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        if (key === "recipt" && values.recipt?.[0]?.originFileObj) {
          formData.append("recipt", values.recipt[0].originFileObj);
        } else if (key !== "groupincharge") {
          formData.append(key, values[key] ?? "");
        }
      });

      const url = editMode
        ? `http://103.61.224.178:9876/member/upsantha/${currentRecord._id}`
        : `http://103.61.224.178:9876/member/createsanta`;
      const response = await axios({
        method: editMode ? "PUT" : "POST",
        url,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      message.success(editMode ? "Santha updated successfully" : "Santha added successfully");
      fetchData();
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error(`Failed to save Santha: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: "Are you sure you want to delete this Santha record?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await axios.delete(`http://103.61.224.178:9876/member/delsantha/${id}`);
          message.success("Santha deleted successfully");
          fetchData();
        } catch (error) {
          message.error(`Failed to delete Santha: ${error.response?.data?.message || error.message}`);
        }
      },
    });
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Santha Transactions", 20, 10);
    const tableColumn = [
      "S.No",
      "ACCI Code",
      "Santha Type",
      "Member Name",
      "Group Incharge",
      "Admin Fee",
      "Year Subscription",
      "Meeting Fee",
      "Other",
      "Remarks",
    ];
    const tableRows = filteredData.map((record, index) => [
      index + 1,
      record.memberId?.Accno || "",
      santhaOptions.find((s) => s.value === record.santhaname)?.label || "",
      memberOptions.find((m) => m.value === record.memberId?._id)?.label || "",
      memberOptions.find((m) => m.value === record.memberId?._id)?.groupincharge || "",
      record.Adminfee || 0,
      record.yearsub || 0,
      record.meetingfee || 0,
      record.other || 0,
      record.remarks || "",
    ]);
    doc.autoTable({ head: [tableColumn], body: tableRows });
    doc.save("santha_transactions.pdf");
  };

  const exportExcel = () => {
    const excelData = filteredData.map((record, index) => {
      const member = memberOptions.find((m) => m.value === record.memberId?._id);
      return {
        "S.No": index + 1,
        "ACCI Code": record.memberId?.Accno || "",
        "Santha Type": santhaOptions.find((s) => s.value === record.santhaname)?.label || "",
        "Member Name": member?.label || "",
        "Group Incharge": member?.groupincharge || "",
        "Admin Fee": record.Adminfee || 0,
        "Year Subscription": record.yearsub || 0,
        "Meeting Fee": record.meetingfee || 0,
        "Other": record.other || 0,
        "Outstanding": record.outstanding || 0,
        "Remarks": record.remarks || "",
      };
    });

    const totalRow = {
      "S.No": "",
      "ACCI Code": "",
      "Santha Type": "",
      "Member Name": "Total",
      "Group Incharge": "",
      "Admin Fee": filteredData.reduce((sum, record) => sum + (parseFloat(record.Adminfee) || 0), 0).toFixed(2),
      "Year Subscription": filteredData.reduce((sum, record) => sum + (parseFloat(record.yearsub) || 0), 0).toFixed(2),
      "Meeting Fee": filteredData.reduce((sum, record) => sum + (parseFloat(record.meetingfee) || 0), 0).toFixed(2),
      "Other": filteredData.reduce((sum, record) => sum + (parseFloat(record.other) || 0), 0).toFixed(2),
      "Outstanding": filteredData.reduce((sum, record) => sum + (parseFloat(record.outstanding) || 0), 0).toFixed(2),
      "Remarks": "",
    };

    const worksheet = XLSX.utils.json_to_sheet([...excelData, totalRow]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Santha Transactions");
    XLSX.writeFile(workbook, "santha_transactions.xlsx");
  };

  const numberToWords = (num) => {
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const units = ["", "Thousand", "Million", "Billion"];

    if (num === 0) return "Zero";

    const convertLessThanThousand = (n) => {
      if (n === 0) return "";
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${ones[n % 10]}`.trim();
      return `${ones[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim();
    };

    const integerPart = Math.floor(num);
    let words = "";
    let n = integerPart;

    if (n === 0) words = "Zero";
    else {
      let i = 0;
      while (n > 0) {
        if (n % 1000 !== 0) {
          words = `${convertLessThanThousand(n % 1000)} ${units[i]} ${words}`.trim();
        }
        n = Math.floor(n / 1000);
        i++;
      }
    }

    return words;
  };

  const getBase64Image = async (url) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Failed to convert image to Base64"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error(`Failed to fetch image from ${url}:`, error);
      return null;
    }
  };

  const printPDF = async (record) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();

    const acciLogoUrl = "http://103.61.224.178:8032/logo.png";
    const acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      const imgWidth = 30;
      const imgHeight = 30;
      doc.addImage(acciLogo, "PNG", 20, 10, imgWidth, imgHeight);
    }

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("AGAMUDAYAR", pageWidth / 2, 18, { align: "center" });

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("CHAMBER OF COMMERCE AND INDUSTRIES", pageWidth / 2, 25, { align: "center" });

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Santha Transaction Invoice", pageWidth / 2, 40, { align: "center" });

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text("1st floor, Madurai press club, Press Colony", 20, 50);
    doc.text("Opp to Mattuthavani bus stand, Madurai", 20, 56);

    doc.text(`Date: ${new Date().toLocaleDateString()}`, 150, 50);
    doc.text(`Invoice No: ${record.invoice || ""}`, 150, 56);

    const member = memberOptions.find((m) => m.value === record.memberId?._id);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Member Details:", 20, 65);

    doc.setFont("helvetica", "normal");
    doc.text(`ACCI Code: ${record.memberId?.Accno || ""}`, 20, 72);
    doc.text(`Member Name: ${member?.label || ""}`, 20, 79);
    doc.text(`Group Incharge: ${member?.groupincharge || ""}`, 20, 86);
    doc.text(`Santha Type: ${santhaOptions.find((s) => s.value === record.santhaname)?.label || ""}`, 20, 93);

    const adminFee = parseFloat(record.Adminfee) || 0;
    const yearSub = parseFloat(record.yearsub) || 0;
    const meetingFee = parseFloat(record.meetingfee) || 0;
    const other = parseFloat(record.other) || 0;
    const totalCollection = (adminFee + yearSub + meetingFee + other).toFixed(2);

    const tableColumn = ["Description", "Amount"];
    const tableRows = [
      ["Admin Fee", adminFee.toFixed(2)],
      ["Year Subscription", yearSub.toFixed(2)],
      ["Meeting Fee", meetingFee.toFixed(2)],
      ["Other", other.toFixed(2)],
      ["Total Collection", totalCollection],
      ["To Be Collect", (parseFloat(record.tobeCollect) || 0).toFixed(2)],
      ["Outstanding", (parseFloat(record.outstanding) || 0).toFixed(2)],
    ];
    if (record.remarks) {
      tableRows.push(["Remarks", record.remarks]);
    }

    const tableStartY = 100;
    doc.autoTable({
      startY: tableStartY,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      margin: { left: 20, right: 20 },
    });

    const finalY = doc.lastAutoTable.finalY || tableStartY + 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`In words: ${numberToWords(parseFloat(totalCollection))} Only`, 20, finalY + 10);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.height;

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text("Thank you for your payment!", pageWidth / 2, pageHeight - 40, { align: "center" });

      doc.setFontSize(8);
      doc.text("Administration office", 20, pageHeight - 24);
      doc.text("1st floor, Madurai press club, Press Colony Opp to Mattuthavani bus stand, Madurai", 20, pageHeight - 32);
      doc.text("Phone: +91 9842143888", 20, pageHeight - 16);
      doc.text(`Page ${i} of ${pageCount}`, 180, pageHeight - 16);
    }

    const pdfBlob = doc.output("blob");
    const pdfURL = URL.createObjectURL(pdfBlob);
    window.open(pdfURL, "_blank");
  };

 const printPDFA5 = async (record) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [72, 180],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.height;

  // LOGO - Top Left (TIGHT)
    const acciLogoUrl = "http://103.61.224.178:8032/logo.png";
    const acciLogo = await getBase64Image(acciLogoUrl);
    if (acciLogo) {
      const imgWidth = 18;
      const imgHeight = 18;
      doc.addImage(acciLogo, "PNG", 5, 5, imgWidth, imgHeight);
    }


  // HEADER - CENTERED & COMPACT
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("AGAMUDAYAR", pageWidth / 2, 22, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text("CHAMBER OF COMMERCE AND INDUSTRIES", pageWidth / 2, 27, { align: "center" });

  // TITLE - CENTERED
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("SANTHA TRANSACTION INVOICE", pageWidth / 2, 33, { align: "center" });

  // DATE & INVOICE - LEFT SIDE
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, 5, 39);
  doc.text(`Invoice No: ${record.invoice || ""}`, 5, 44);

  // MEMBER DETAILS - COMPACT
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.text("MEMBER DETAILS", 5, 49);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  const member = memberOptions.find((m) => m.value === record.memberId?._id);
  
  doc.text(`ACCI Code: ${record.memberId?.Accno || ""}`, 5, 54);
  doc.text(`Member: ${member?.label || ""}`, 5, 58);
  doc.text(`Incharge: ${member?.groupincharge || ""}`, 5, 62);
  doc.text(
    `Santha: ${santhaOptions.find((s) => s.value === record.santhaname)?.label || ""}`,
    5, 66
  );

  // CALCULATIONS
  const adminFee = parseFloat(record.Adminfee) || 0;
  const yearSub = parseFloat(record.yearsub) || 0;
  const meetingFee = parseFloat(record.meetingfee) || 0;
  const other = parseFloat(record.other) || 0;
  const totalCollection = adminFee + yearSub + meetingFee + other;
  const toBeCollect = parseFloat(record.tobeCollect) || 0;
  const outstanding = parseFloat(record.outstanding) || 0;

  // **SUPER COMPACT TABLE** - Fits everything in 1 page
  const tableColumn = ["DESCRIPTION", "AMOUNT"];
  const tableRows = [
    ["Admin Fee", adminFee.toFixed(2)],
    ["Year Sub", yearSub.toFixed(2)],
    ["Meeting Fee", meetingFee.toFixed(2)],
    ["Other", other.toFixed(2)],
    ["TOTAL", totalCollection.toFixed(2)],
    ["To Collect", toBeCollect.toFixed(2)],
    ["Outstanding", outstanding.toFixed(2)],
  ];

  if (record.remarks && record.remarks.trim()) {
    tableRows.push(["Remarks", record.remarks.trim()]);
  }

  const tableStartY = 71; // **TIGHT START**
  doc.autoTable({
    startY: tableStartY,
    head: [tableColumn],
    body: tableRows,
    theme: "grid",
    styles: { 
      fontSize: 6, // **SMALLER for fit**
      cellPadding: 1.2, // **TIGHTER**
      lineWidth: 0.1
    },
    headStyles: { 
      fillColor: [41, 128, 185], 
      textColor: 255,
      fontSize: 6.5,
      fontStyle: 'bold'
    },
    margin: { left: 5, right: 5 },
    columnStyles: { 
      0: { cellWidth: 35, halign: 'left' },
      1: { cellWidth: 27, halign: 'right' } 
    },
    tableLineWidth: 0.3,
  });

  // **AMOUNT IN WORDS** - Very tight
  const finalY = doc.lastAutoTable.finalY || tableStartY;
  doc.setFontSize(6.5);
  doc.setFont("helvetica", "italic");
  doc.text(
    `₹ ${numberToWords(Math.round(totalCollection))} ONLY`, 
    5, finalY + 3
  );

  // **FOOTER** - Bottom fixed position
  const footerY = pageHeight - 32;
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text("THANK YOU!", pageWidth / 2, footerY - 2, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.text("Madurai Press Club, 1st Floor", pageWidth / 2, footerY + 2, { align: "center" });
  doc.text("Opp. Mattuthavani Bus Stand", pageWidth / 2, footerY + 5, { align: "center" });
  doc.text("Ph: 98421 43888", pageWidth / 2, footerY + 8, { align: "center" });

  // **Page number**
  doc.setFontSize(6);
  doc.text("Page 1", pageWidth - 15, pageHeight - 2);

  // **GUARANTEE: SINGLE PAGE**
  console.log("Final Y position:", finalY, "Page Height:", pageHeight);
  
  const pdfBlob = doc.output("blob");
  const pdfURL = URL.createObjectURL(pdfBlob);
  window.open(pdfURL, "_blank");
};
  const columns = [
    { title: "S.No", render: (_, __, index) => index + 1 },
    {
      title: "ACCI Code",
      dataIndex: ["memberId", "Accno"],
      sorter: (a, b) => (a.memberId?.Accno || "").localeCompare(b.memberId?.Accno || ""),
    },
    {
      title: "Santha Type",
      dataIndex: "santhaname",
      render: (id) => santhaOptions.find((s) => s.value === id)?.label || "",
    },
    {
      title: "Member Name",
      dataIndex: "memberId",
      render: (member) => memberOptions.find((m) => m.value === member?._id)?.label || "",
    },
    {
      title: "Group Incharge",
      dataIndex: "memberId",
      render: (member) => memberOptions.find((m) => m.value === member?._id)?.groupincharge || "",
    },
    {
      title: "Admin Fee",
      dataIndex: "Adminfee",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Year Subscription",
      dataIndex: "yearsub",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Meeting Fee",
      dataIndex: "meetingfee",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Other",
      dataIndex: "other",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "To Be Collect",
      dataIndex: "tobeCollect",
      render: (value) => (parseFloat(value) || 0).toFixed(2),
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      render: (value) => {
        const outstandingValue = parseFloat(value) || 0;
        const color = outstandingValue < 0 ? "green" : outstandingValue > 0 ? "red" : "black";
        return <span style={{ color }}>{outstandingValue.toFixed(2)}</span>;
      },
    },
    { title: "Remarks", dataIndex: "remarks" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={() => printPDF(record)}
            title="Print A4"
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={() => printPDFA5(record)}
            title="Print A5"
            style={{ color: "green" }}
          />
        </Space>
      ),
    },
  ];

  const normFile = (e) => (Array.isArray(e) ? e : []);

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Input
            placeholder="Search Santha"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col span={4}>
          <Select
            placeholder="Filter by Santha Master"
            allowClear
            style={{ width: "100%" }}
            onChange={(value) => setSelectedSanthaFilter(value || null)}
            options={santhaOptions}
          />
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditMode(false);
              form.resetFields();
              form.setFieldsValue({ tobeCollect: "0.00", outstanding: "0.00", Adminfee: "0.00" });
              setIsModalVisible(true);
            }}
          >
            Add Santha
          </Button>
          <Button icon={<FilePdfOutlined />} onClick={exportPDF} style={{ marginLeft: 8 }}>
            Export PDF
          </Button>
          <Button icon={<FileExcelOutlined />} onClick={exportExcel} style={{ marginLeft: 8, color: "green" }}>
            Export Excel
          </Button>
        </Col>
      </Row>
      <Table columns={columns} dataSource={filteredData} rowKey="_id" />
      <Modal
        title={editMode ? "Edit Santha" : "Add Santha"}
        open={isModalVisible}
        onOk={() => form.validateFields().then(handleAddOrUpdate)}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        okButtonProps={{ loading }}
      >
        <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Form.Item
            label="Santha Type"
            name="santhaname"
            rules={[{ required: true, message: "Please select Santha Type" }]}
          >
            <Select options={santhaOptions} placeholder="Select Santha Type" />
          </Form.Item>
          <Form.Item
            label="Member"
            name="memberId"
            rules={[{ required: true, message: "Please select Member" }]}
          >
            <Select
              showSearch
              placeholder="Select Member"
              optionFilterProp="label"
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              options={memberOptions}
            />
          </Form.Item>
          <Form.Item label="Group Incharge" name="groupincharge">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="To Be Collect"
            name="tobeCollect"
            rules={[
              { required: true, message: "Please enter To Be Collect" },
              { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
            ]}
          >
            <Input type="number" step="0.01" min="0" />
          </Form.Item>
          <Form.Item
            label="Admin Fee"
            name="Adminfee"
            rules={[
              { required: true, message: "Please enter Admin Fee" },
              { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
            ]}
          >
            <Input type="number" step="0.01" min="0" />
          </Form.Item>
          <Form.Item
            label="Year Subscription"
            name="yearsub"
            rules={[
              { required: true, message: "Please enter Year Subscription" },
              { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
            ]}
          >
            <Input type="number" step="0.01" min="0" />
          </Form.Item>
          <Form.Item
            label="Meeting Fee"
            name="meetingfee"
            rules={[
              { required: true, message: "Please enter Meeting Fee" },
              { pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" },
            ]}
          >
            <Input type="number" step="0.01" min="0" />
          </Form.Item>
          <Form.Item label="Outstanding" name="outstanding">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="Other"
            name="other"
            rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: "Please enter a valid number" }]}
          >
            <Input type="number" step="0.01" min="0" />
          </Form.Item>
          <Form.Item
            label="Receipt"
            name="recipt"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload
              beforeUpload={() => false}
              listType="picture"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload Receipt</Button>
            </Upload>
          </Form.Item>
          <Form.Item label="Remarks" name="remarks">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Santha;